import React, { useState } from 'react';
import { BannerSet, BannerSize } from '../types';
import { X, Download, FileText, Image as ImageIcon, CheckSquare, Square } from 'lucide-react';
import { exportBannerSet } from '../utils/exportUtils';
import { useToast } from '../contexts/ToastContext';

interface BannerSetExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  bannerSet: BannerSet;
}

export const BannerSetExportModal: React.FC<BannerSetExportModalProps> = ({
  isOpen,
  onClose,
  bannerSet
}) => {
  const [selectedFormat, setSelectedFormat] = useState<'html' | 'png' | 'jpeg'>('png');
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [quality, setQuality] = useState(90);
  const [isExporting, setIsExporting] = useState(false);
  
  const { showSuccess, showError, showInfo } = useToast();

  // Get completed sizes only
  const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);

  // Initialize with all completed sizes selected
  React.useEffect(() => {
    if (completedSizes.length > 0 && selectedSizes.length === 0) {
      setSelectedSizes(completedSizes.map(size => size.id));
    }
  }, [completedSizes, selectedSizes.length]);

  if (!isOpen) return null;

  const handleSizeToggle = (sizeId: string) => {
    setSelectedSizes(prev => 
      prev.includes(sizeId) 
        ? prev.filter(id => id !== sizeId)
        : [...prev, sizeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSizes.length === completedSizes.length) {
      setSelectedSizes([]);
    } else {
      setSelectedSizes(completedSizes.map(size => size.id));
    }
  };

  const handleExport = async () => {
    if (selectedSizes.length === 0) {
      showError('Export Error', 'Please select at least one banner size to export.');
      return;
    }

    if (bannerSet.dataFeedArray.length === 0) {
      showError('Export Error', 'Please upload a data feed first to export banners.');
      return;
    }

    setIsExporting(true);
    
    try {
      showInfo('Export Started', `Generating ${getTotalExportCount()} files. This may take a moment...`);
      
      const selectedSizeObjects = completedSizes.filter(size => selectedSizes.includes(size.id));
      
      await exportBannerSet({
        bannerSet,
        selectedSizes: selectedSizeObjects,
        format: selectedFormat,
        quality: selectedFormat === 'jpeg' ? quality : undefined
      });

      showSuccess('Export Complete', `Successfully exported ${getTotalExportCount()} files as a ZIP archive.`);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      showError('Export Failed', 'An error occurred while exporting the banner set. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const formatOptions = [
    { 
      value: 'html', 
      label: 'HTML5', 
      icon: FileText, 
      description: 'Interactive banners with animations',
      extension: '.html'
    },
    { 
      value: 'png', 
      label: 'PNG Images', 
      icon: ImageIcon, 
      description: 'High quality images with transparency',
      extension: '.png'
    },
    { 
      value: 'jpeg', 
      label: 'JPEG Images', 
      icon: ImageIcon, 
      description: 'Compressed images for web use',
      extension: '.jpg'
    }
  ];

  const getTotalExportCount = () => {
    return selectedSizes.length * bannerSet.dataFeedArray.length;
  };

  const getEstimatedFileSize = () => {
    const totalFiles = getTotalExportCount();
    const avgSizePerFile = selectedFormat === 'html' ? 50 : selectedFormat === 'png' ? 500 : 200; // KB
    const totalSizeKB = totalFiles * avgSizePerFile;
    
    if (totalSizeKB > 1024) {
      return `~${(totalSizeKB / 1024).toFixed(1)} MB`;
    }
    return `~${totalSizeKB} KB`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Export Banner Set</h2>
            <p className="text-sm text-gray-600 mt-1">{bannerSet.name}</p>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Export Format Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Export Format</h3>
            <div className="grid grid-cols-1 gap-3">
              {formatOptions.map((format) => (
                <button
                  key={format.value}
                  onClick={() => setSelectedFormat(format.value as any)}
                  disabled={isExporting}
                  className={`p-4 border rounded-lg text-left transition-colors disabled:opacity-50 ${
                    selectedFormat === format.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <format.icon size={20} className={selectedFormat === format.value ? 'text-blue-600' : 'text-gray-500'} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{format.label}</span>
                        <span className="text-xs text-gray-500">{format.extension}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{format.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quality Setting for JPEG */}
          {selectedFormat === 'jpeg' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Image Quality</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Quality: {quality}%</label>
                  <span className="text-xs text-gray-500">
                    {quality >= 90 ? 'High' : quality >= 70 ? 'Medium' : 'Low'}
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={quality}
                  onChange={(e) => setQuality(parseInt(e.target.value))}
                  disabled={isExporting}
                  className="w-full disabled:opacity-50"
                />
              </div>
            </div>
          )}

          {/* Banner Size Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Select Banner Sizes</h3>
              <button
                onClick={handleSelectAll}
                disabled={isExporting}
                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
              >
                {selectedSizes.length === completedSizes.length ? (
                  <>
                    <CheckSquare size={16} />
                    <span>Deselect All</span>
                  </>
                ) : (
                  <>
                    <Square size={16} />
                    <span>Select All</span>
                  </>
                )}
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {completedSizes.map((size) => (
                <label
                  key={size.id}
                  className={`flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                    isExporting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSizes.includes(size.id)}
                    onChange={() => handleSizeToggle(size.id)}
                    disabled={isExporting}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{size.name}</span>
                      <span className="text-sm text-gray-500">{size.width} × {size.height}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {size.elements.length} elements • {size.category} • {bannerSet.dataFeedArray.length} variations
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {completedSizes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>No completed banner sizes available for export.</p>
                <p className="text-sm mt-1">Please design at least one banner first.</p>
              </div>
            )}
          </div>

          {/* Export Summary */}
          {selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Export Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <div>Selected sizes: {selectedSizes.length} of {completedSizes.length}</div>
                <div>Data variations: {bannerSet.dataFeedArray.length}</div>
                <div className="font-medium">Total files to export: {getTotalExportCount()}</div>
                <div>Estimated size: {getEstimatedFileSize()}</div>
                <div className="text-xs text-blue-600 mt-2">
                  Files will be organized by size and variation in a ZIP archive.
                </div>
              </div>
            </div>
          )}

          {/* Data Feed Warning */}
          {bannerSet.dataFeedArray.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-1">No Data Feed</h4>
              <p className="text-sm text-yellow-700">
                Please upload a data feed to enable batch export functionality. 
                Without a data feed, only static banners can be exported.
              </p>
            </div>
          )}

          {/* File Naming Convention Info */}
          {selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">File Naming Convention</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Format: <code className="bg-white px-1 rounded text-xs">size-name_variation-number_model-name.{selectedFormat === 'html' ? 'html' : selectedFormat === 'png' ? 'png' : 'jpg'}</code></div>
                <div>Example: <code className="bg-white px-1 rounded text-xs">1080x1080_001_accord-hybrid-sport.{selectedFormat === 'html' ? 'html' : selectedFormat === 'png' ? 'png' : 'jpg'}</code></div>
                <div className="text-xs text-gray-500 mt-2">
                  Files will be organized in folders by banner size for easy management.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {isExporting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Generating files...</span>
              </div>
            ) : selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 ? (
              `Ready to export ${getTotalExportCount()} files`
            ) : (
              'Select sizes and ensure data feed is uploaded'
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={selectedSizes.length === 0 || bannerSet.dataFeedArray.length === 0 || isExporting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={16} />
              <span>{isExporting ? 'Exporting...' : 'Export Selected'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};